<template>
  <div :class="['savedSearchPopup', {error: hasError}]">
    <div :class="['savedSearchPopupContainer', { shown: isVisible }]">
      <div class="topTabs">
        <span :class="['tab', 'tabLeft', { active: tab === 'history' }]" @click="switchTabs">
          <div><i class="far fa-history"></i></div>
          <div>Recent History</div>
        </span>
        <span :class="['tab', 'tabRight', { active: tab === 'favorites' }]" @click="switchTabs">
          <div><i class="far fa-book-heart"></i></div>
          <div>Saved Searches</div>
        </span>
        <span class="closeButton" @click="handleClose">
          <i class="fal fa-times-circle" @click="handleClose"></i>
        </span>
      </div>
      <div class="content">
        <div class="historyTab" v-if="tab === 'history'">
          <ul class="list historyDays">
            <li v-for="(historyList, day, index) in historyMap" :key="day">
              <span class="dayDescription">{{day}}</span>
              <hr class="dayBreak"></hr>
              <ul class="list historyEntries">
                <li v-for="history in historyList" class="historyEntry">
                  <span class="historyTime">{{ calculateTime(history.created) }}</span>
                  <span class="historyName" v-dompurify-html="history.name" @click="enableHistory(history)" title="Enable"></span>
                  <span class="menuActivator">
                    <i class="dropdown-toggle far fa-ellipsis-h" data-toggle="dropdown"></i>
                    <ul class="dropdown-menu" role="menu">
                        <li @click="enableHistory(history)" class="border"><i class="far fa-plus-circle"></i>Enable</li>
                        <li @click="deleteHistory(history)"><i class="far fa-trash-alt"></i>Delete</li>
                    </ul>
                  </span>
                </li>
              </ul>
            </li>
          </ul>
          <footer class="limitMessage footer" v-if="history.length >= getHistoryLimit()">
            Need more recent history? <a target="_blank" href="https://www.vyopta.com/product/cpm/how-to-buy/">See upgrade options</a>
          </footer>
        </div>
        <div class="favoritesTab" v-if="tab === 'favorites'">
          <ul class="list favorites">
            <li class="favoriteEntry adding" v-if="isAdding && favorites.length < getFavoritesLimit()">
              <span class="favoriteIcon"><i class="fas fa-bookmark"></i></span>
              <input id="addingInput" type="text" v-model="newFavoriteName" v-on:keyup.enter="handleAdd" />
              <i class="fas fa-check-circle" @click="handleAdd"></i>
            </li>
            <li class="favoriteEntry adding" v-if="isAdding && favorites.length >= getFavoritesLimit()">
              <span class="fullText alert alert-warning">You have reached the saved search limit</span>
            </li>
            <li class="favoriteEntry" v-for="(favorite, i) in currentFavorites" :key="favorite.created.toString()+favorite.isEditing">
              <span class="favoriteIcon"><i class="fal fa-bookmark"></i></span>
              <span v-if="!favorite.isEditing" class="favoriteName" @click="enableFavorite(favorite)" title="Enable" :key="favorite.isEditing">{{favorite.name}}</span>
              <input :ref="'editingInput'+i" v-show="favorite.isEditing" class="editingInput" type="text" v-model="favorite.name" v-on:keyup.enter="handleRename(favorite)"/>
              <i v-if="favorite.isEditing" class="fas fa-check-circle" @click="handleRename(favorite)"></i>
              <span v-if="!favorite.isEditing" class="menuActivator dropdown">
                <i class="dropdown-toggle far fa-ellipsis-h" data-toggle="dropdown"></i>
                <ul class="dropdown-menu" role="menu">
                    <li @click="enableFavorite(favorite)" class="border"><i class="far fa-plus-circle"></i>Enable</li>
                    <li @click="renameFavorite(favorite, i)" class="border"><i class="far fa-edit"></i>Rename</li>
                    <li @click="deleteFavorite(favorite)"><i class="far fa-trash-alt"></i>Delete</li>
                </ul>
              </span>
            </li>
          </ul>
          <footer class="limitMessage footer" v-if="favorites.length >= getFavoritesLimit()">
            Need more saved searches? <a target="_blank" href="https://www.vyopta.com/product/cpm/how-to-buy/">See upgrade options</a>
          </footer>
        </div>
        <div class="errorMessage">
          {{errorMessage}}
        </div>
      </div>
    </div>
    <popup-wrapper :class="{hidden: !isVisible}" @popup-mask-clicked="handleClose">
    </popup-wrapper>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import PopupWrapper from '../popupWrapper/popupWrapper.vue';

const DIAL_BACK_TO_FAVORITE_LIMITS = {
  4: 1,
  24: 5,
  168: 10,
  720: 1000,
};
const DIAL_BACK_TO_HISTORY_LIMITS = {
  4: 10,
  24: 10,
  168: 1000,
  720: 1000,
};

function makeHistoryMap(historyArray) {
  let historyMap = {};
  if (!historyArray || !historyArray.reverse) {
    return;
  }
  let copy = [...historyArray];
  copy.reverse();
  copy.forEach((state) => {
    let day = moment(state.created).format('DD MMM YYYY');
    if (historyMap[day]) {
      historyMap[day].push(state);
    } else {
      historyMap[day] = [state];
    }
  });
  return historyMap;
}

export default {
  name: 'SavedSearchPopup',
  props: {
    activeTab: {
      type: String,
      default: false
    },
    isAdding: {
      type: Boolean,
      default: false
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    history: {
      type: Array,
      default: () => []
    },
    favorites: {
      type: Array,
      default: () => []
    },
    dialBackHours: {
      type: Number,
      default: 720
    },
  },
  data: function() {
    return {
      tab: this.activeTab,
      errorMessage: '',
      hasError: false,
      newFavoriteName: 'Untitiled',
    };
  },
  computed: {
    historyMap: function() {
      return makeHistoryMap(this.history);
    },
    currentFavorites: function() {
      let copy = [...this.favorites];
      return copy.reverse();
    }
  },
  watch: {
    isAdding: function(isAdding) {
      if (isAdding) {
        window.setTimeout(() => {
          let input = document.getElementById('addingInput');
          if (input) {
            input.focus();
            input.select();
          }
        }, 100);
      }
    },
    activeTab: function(activeTab) {
      this.tab = activeTab;
    },
  },
  methods: {
    calculateTime: (timestamp) => {
      return moment(timestamp).format('hh:mm:ss');
    },
    deleteFavorite: function(favorite) {
      this.$emit('delete-favorite', favorite);
    },
    deleteHistory: function(history) {
      this.$emit('delete-history', history);
    },
    enableFavorite: function(favorite) {
      this.$emit('enable-favorite', favorite);
    },
    enableHistory: function(history) {
      this.$emit('enable-history', history);
    },
    handleAdd: function() {
      let name = this.newFavoriteName;
      this.newFavoriteName = 'Untitled';
      this.$emit('favorite-added', name);
    },
    handleClose: function() {
      this.newFavoriteName = 'Untitled';
      this.$emit('popup-close');
    },
    handleRename: function(favorite) {
      favorite.isEditing = false;
      this.$emit('favorite-renamed', favorite);
      this.$forceUpdate();
    },
    getFavoritesLimit: function() {
      return DIAL_BACK_TO_FAVORITE_LIMITS[this.dialBackHours] || 1;
    },
    getHistoryLimit: function() {
      return DIAL_BACK_TO_HISTORY_LIMITS[this.dialBackHours] || 10;
    },
    renameFavorite: function(favorite, i) {
      favorite.isEditing = true;
      let self = this;
      window.setTimeout(() => {
        let input = self.$refs['editingInput'+i][0];
        if (input) {
          input.focus();
          input.select();
        }
      }, 100);
      this.$forceUpdate();
    },
    switchTabs: function() {
      if (this.tab === 'history') {
        this.tab = 'favorites';
      } else {
        this.tab = 'history';
      }
    },
  },
  components: {
    'popup-wrapper': PopupWrapper,
  },
};
</script>
